import { useOrganization } from '@clerk/react';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCallingAccess } from '@/containers/settings/calling/use-calling-access.hook';
import { formatPhone } from '@/lib/format-phone';
import { iconPhone } from '@/lib/icons';
import { cn } from '@/lib/utils';
import {
  useGetMyNumberQuery,
  useListMyCallsQuery,
  type CallRecord,
} from '@/store/features/telephony.feature';
import { ACTIVE_PHASES, useCall } from './call-context';
import { KEYS, endedSummary } from './call-format';

/** Recent numbers the popover offers to call again. */
const RECENT = 5;

/** The other party's number on each call, newest first, once each. */
function recentNumbers(calls: CallRecord[]): CallRecord[] {
  const seen = new Set<string>();
  const out: CallRecord[] = [];
  for (const call of calls) {
    const number = call.direction === 'outbound' ? call.toE164 : call.fromE164;
    if (!number || seen.has(number)) continue;
    seen.add(number);
    out.push(call);
    if (out.length === RECENT) break;
  }
  return out;
}

/**
 * The dialer's way in from the sidebar: a phone button that opens a keypad
 * and the rep's recent calls. Shown to anyone whose org has calling; a rep
 * without a number yet gets a link to set one up instead of the keypad.
 */
export function DialerButton({ rail = false }: { rail?: boolean }) {
  const access = useCallingAccess();
  const [open, setOpen] = useState(false);
  if (!access.enabled) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type='button'
              aria-label='Dialer'
              className={cn(
                'relative grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors',
                'text-gray-600 hover:bg-black/[0.05] dark:text-gray-400 dark:hover:bg-white/[0.06]',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
              )}
            >
              <AppIcon icon={iconPhone} style={{ fontSize: rail ? 16 : 15 }} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side={rail ? 'right' : 'top'} sideOffset={6}>
          Dialer
        </TooltipContent>
      </Tooltip>
      <PopoverContent side='right' align='end' className='w-72 p-3'>
        {open && <DialerPanel onPlaced={() => setOpen(false)} />}
      </PopoverContent>
    </Popover>
  );
}

function DialerPanel({ onPlaced }: { onPlaced: () => void }) {
  const { organization } = useOrganization();
  const organizationId = organization?.id ?? '';
  const { available, call, start } = useCall();
  const { currentData: mine } = useGetMyNumberQuery(organizationId, {
    skip: !organizationId,
  });
  const { currentData: calls } = useListMyCallsQuery(organizationId, {
    skip: !organizationId || !available,
  });
  const [number, setNumber] = useState('');
  const busy = !!call && ACTIVE_PHASES.has(call.phase);

  if (!available) {
    // Still loading: say nothing rather than flash the setup prompt.
    if (!mine) return null;
    return (
      <div className='space-y-2 text-sm'>
        <p className='font-medium'>Call from Cleanlist</p>
        <p className='text-xs text-muted-foreground'>
          Get a phone number to call leads from your browser.
        </p>
        <Button asChild size='sm' className='w-full'>
          <Link to='/settings/calling' onClick={onPlaced}>
            Set up calling
          </Link>
        </Button>
      </div>
    );
  }

  const dial = (to: string, label?: string) => {
    if (!to.trim() || busy) return;
    onPlaced();
    void start({ to: to.trim(), label });
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    dial(number);
  };
  const recent = calls ? recentNumbers(calls) : [];

  return (
    <div className='space-y-3'>
      <form onSubmit={submit} className='space-y-2'>
        <Input
          type='tel'
          inputMode='tel'
          autoFocus
          aria-label='Number to call'
          placeholder='Number to call'
          value={number}
          onChange={(event) => setNumber(event.target.value)}
          className='h-9 text-center font-mono tracking-wide'
        />
        <div className='grid grid-cols-3 gap-1.5'>
          {KEYS.map((key) => (
            <button
              key={key}
              type='button'
              className='h-9 rounded-md border border-border text-sm font-medium hover:bg-muted'
              onClick={() => setNumber((value) => value + key)}
            >
              {key}
            </button>
          ))}
        </div>
        <Button
          type='submit'
          size='sm'
          className='w-full gap-1.5'
          disabled={!number.trim() || busy}
        >
          <AppIcon icon={iconPhone} style={{ fontSize: 12 }} />
          {busy ? 'On a call' : 'Call'}
        </Button>
      </form>
      {recent.length > 0 && (
        <div>
          <p className='mb-1 text-[11px] font-medium text-muted-foreground'>
            Recent
          </p>
          <ul>
            {recent.map((record) => {
              const to =
                record.direction === 'outbound'
                  ? record.toE164
                  : record.fromE164;
              return (
                <li key={record.id}>
                  <button
                    type='button'
                    disabled={busy}
                    onClick={() => dial(to, formatPhone(to))}
                    className='flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-muted disabled:opacity-50'
                  >
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate text-[13px] tabular-nums'>
                        {formatPhone(to)}
                      </span>
                      <span className='block truncate text-[11px] text-muted-foreground'>
                        {record.disposition ?? endedSummary(record)}
                      </span>
                    </span>
                    <AppIcon
                      icon={iconPhone}
                      className='shrink-0 text-muted-foreground'
                      style={{ fontSize: 11 }}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <Link
        to='/settings/calling'
        onClick={onPlaced}
        className='block text-center text-[11px] text-muted-foreground hover:text-foreground'
      >
        All calls and settings
      </Link>
    </div>
  );
}
